import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, X, Star, Eye, Package, ArrowUpDown, Heart } from 'lucide-react';
import { listingsAPI, photoAPI, marketAPI } from '../lib/api';
import { useFavorites } from '../context/FavoritesContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import SEO from '../components/SEO';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

const condKey = (score) => {
  if (score >= 9) return 'home.cond_new';
  if (score >= 7) return 'home.cond_very_good';
  if (score >= 5) return 'home.cond_good';
  return 'home.cond_fair';
};

const conditionColor = (score) => {
  if (score >= 10) return 'bg-emerald-500';
  if (score >= 9) return 'bg-green-500';
  if (score >= 7) return 'bg-sky-500';
  if (score >= 5) return 'bg-amber-500';
  return 'bg-orange-500';
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return i18n.t('cat.just_now');
  if (diff < 3600) return i18n.t('cat.min_ago', { n: Math.floor(diff / 60) });
  if (diff < 86400) return i18n.t('cat.hours_ago', { n: Math.floor(diff / 3600) });
  if (diff < 2592000) return i18n.t('cat.days_ago', { n: Math.floor(diff / 86400) });
  return new Date(dateStr).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : `${i18n.language}-${i18n.language.toUpperCase()}`, { day: 'numeric', month: 'short' });
}

const ListingCard = ({ listing }) => {
  const { t } = useTranslation();
  const photoUrl = listing.photos?.[0] ? photoAPI.getUrl(listing.photos[0]) : null;
  const { isFavorite, toggleFavorite } = useFavorites();
  const { isAuthenticated } = useAuth();
  const fav = isFavorite(listing.id);
  const isSold = listing.status === 'sold';

  const handleFav = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) return;
    toggleFavorite(listing.id);
  };

  return (
    <Link to={`/annonce/${listing.id}`} className="group" data-testid={`listing-card-${listing.id}`}>
      <div className={`bg-white rounded-2xl border border-[#E8F4FD] overflow-hidden hover:shadow-xl hover:border-transparent transition-all duration-300 hover:-translate-y-1 ${isSold ? 'opacity-75' : ''}`}>
        <div className="aspect-[4/3] bg-[#F0F7FA] relative overflow-hidden">
          {photoUrl ? (
            <img src={photoUrl} alt={listing.model} className={`w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 ${isSold ? 'grayscale' : ''}`} />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#B8D4E3]">
              <Package className="w-14 h-14" />
            </div>
          )}
          {isSold ? (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
              <span className="bg-red-500 text-white text-sm font-bold px-4 py-2 rounded-lg transform -rotate-12 shadow-lg">
                {t('cat.sold')}
              </span>
            </div>
          ) : (
            <>
              <span className={`absolute top-2.5 left-2.5 px-2.5 py-1 rounded-lg text-[10px] font-bold text-white shadow-sm ${conditionColor(listing.condition_score)}`}>
                {t(condKey(listing.condition_score))}
              </span>
              {listing.market_price && listing.price < listing.market_price && (
                <span className="absolute bottom-2.5 left-2.5 px-2 py-1 rounded-lg text-[10px] font-bold bg-red-500 text-white shadow-sm">
                  -{Math.round((1 - listing.price / listing.market_price) * 100)}%
                </span>
              )}
              <button onClick={handleFav}
                className={`absolute top-2.5 right-2.5 w-8 h-8 rounded-full flex items-center justify-center shadow-md transition-all duration-200 ${fav ? 'bg-red-50 scale-110' : 'bg-white/90 backdrop-blur-sm hover:bg-red-50 opacity-0 group-hover:opacity-100'}`}
                data-testid={`fav-toggle-${listing.id}`}>
                <Heart className={`w-4 h-4 transition-colors ${fav ? 'text-red-500 fill-red-500' : 'text-[#5A8BA8]'}`} />
              </button>
            </>
          )}
        </div>
        <div className="p-4">
          <p className="text-[10px] text-[#5A8BA8] uppercase tracking-wider font-semibold">{listing.brand}</p>
          <h3 className="font-bold text-[#033871] text-sm mt-0.5 line-clamp-1 group-hover:text-[#0C63BF] transition-colors">{listing.model.replace(new RegExp('^' + listing.brand + '\\s+', 'i'), '')}</h3>
          <div className="flex items-center gap-2 mt-2.5">
            <span className="text-lg font-black text-[#0C63BF]">{listing.price.toFixed(0)}&euro;</span>
            {listing.market_price && (
              <span className="text-[10px] text-[#B8D4E3] line-through">{listing.market_price.toFixed(0)}&euro; {t('cat.new_price')}</span>
            )}
          </div>
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#F0F7FA]">
            <div className="flex items-center gap-1 text-xs text-[#5A8BA8]">
              {listing.seller_rating ? (
                <>
                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                  <span className="font-medium">{listing.seller_rating.toFixed(1)}</span>
                </>
              ) : (
                <span className="text-[10px]">{t('home.verified_profile')}</span>
              )}
            </div>
            <div className="flex items-center gap-1 text-[10px] text-[#B8D4E3]">
              <Eye className="w-3 h-3" />
              <span>{listing.views}</span>
              {listing.approved_at || listing.created_at ? (
                <span className="ml-1.5">{timeAgo(listing.approved_at || listing.created_at)}</span>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

const CataloguePage = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');

  const [filters, setFilters] = useState({
    brand: searchParams.get('brand') || '',
    shape: searchParams.get('shape') || '',
    play_type: searchParams.get('play_type') || '',
    weight: searchParams.get('weight') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    min_condition: searchParams.get('min_condition') || '',
    search: searchParams.get('search') || '',
    sort_by: searchParams.get('sort_by') || 'created_at',
    sort_order: searchParams.get('sort_order') || 'desc',
    page: parseInt(searchParams.get('page') || '1'),
  });

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = { include_sold: true };
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== '' && v !== null) params[k] = v;
      });
      const res = await listingsAPI.getAll(params);
      setListings(res.data.listings);
      setTotal(res.data.total);
    } catch (err) {
      console.error('Error fetching listings:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    marketAPI.getBrandsModels().then(res => {
      setBrands(Object.keys(res.data).sort());
    }).catch(() => {});
  }, []);

  useEffect(() => {
    fetchListings();
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== 1) params[k] = String(v);
    });
    setSearchParams(params, { replace: true });
  }, [filters, fetchListings, setSearchParams]);

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    updateFilter('search', searchInput);
  };

  const clearFilters = () => {
    setSearchInput('');
    setFilters({ brand: '', shape: '', play_type: '', weight: '', min_price: '', max_price: '', min_condition: '', search: '', sort_by: 'created_at', sort_order: 'desc', page: 1 });
  };

  const hasActiveFilters = filters.brand || filters.shape || filters.play_type || filters.weight || filters.min_price || filters.max_price || filters.min_condition || filters.search;
  const activeFilterCount = [filters.brand, filters.shape, filters.play_type, filters.weight, filters.min_price, filters.max_price, filters.min_condition, filters.search].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#F7FAFB]">
      <SEO
        title={filters.brand ? t('cat.brand_rackets', { brand: filters.brand.charAt(0).toUpperCase() + filters.brand.slice(1) }) : t('seo.catalogue.title').split(' | ')[0]}
        description={t('seo.catalogue.description')}
        path="/catalogue"
      />
      {/* Header */}
      <div className="bg-white border-b border-[#E8F4FD]">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-5">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <h1 className="text-base sm:text-2xl font-black text-[#033871] truncate" data-testid="catalogue-title">
                {filters.search ? t('cat.results_for', { query: filters.search }) : filters.brand ? t('cat.brand_rackets', { brand: filters.brand.charAt(0).toUpperCase() + filters.brand.slice(1) }) : t('cat.all_rackets')}
              </h1>
              <p className="text-[#5A8BA8] mt-0.5 text-sm">
                {total > 1 ? t('cat.available_other', { count: total }) : t('cat.available_one', { count: total })}
              </p>
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" onClick={clearFilters} className="text-[#0C63BF] gap-1 text-sm" data-testid="clear-filters-btn">
                <X className="w-4 h-4" /> {t('cat.reset')}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
        {/* Filters bar */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)}
            className={`gap-2 rounded-xl border-[#D6E8F0] bg-white hover:bg-[#F0F7FA] text-[#033871] hover:text-[#033871] ${showFilters ? 'bg-[#F0F7FA] border-[#0C63BF] text-[#0C63BF] hover:text-[#0C63BF]' : ''}`}
            data-testid="toggle-filters">
            <SlidersHorizontal className="w-4 h-4" /> {t('cat.filters')}
            {activeFilterCount > 0 && (
              <span className="w-5 h-5 bg-[#0C63BF] text-white text-[10px] font-bold rounded-full flex items-center justify-center">{activeFilterCount}</span>
            )}
          </Button>

          <div className="flex items-center gap-2 bg-white rounded-xl border border-[#D6E8F0] px-3 h-10">
            <ArrowUpDown className="w-3.5 h-3.5 text-[#5A8BA8]" />
            <Select value={filters.sort_by} onValueChange={v => updateFilter('sort_by', v)}>
              <SelectTrigger className="border-0 p-0 h-auto shadow-none focus:ring-0 text-sm w-[130px]" data-testid="sort-select">
                <SelectValue placeholder={t('cat.sort_by')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created_at">{t('cat.sort_recent')}</SelectItem>
                <SelectItem value="price">{t('cat.sort_price')}</SelectItem>
                <SelectItem value="condition_score">{t('cat.sort_condition')}</SelectItem>
                <SelectItem value="views">{t('cat.sort_popularity')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Select value={filters.sort_order} onValueChange={v => updateFilter('sort_order', v)}>
            <SelectTrigger className="w-[130px] rounded-xl border-[#D6E8F0] bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="desc">{t('cat.desc')}</SelectItem>
              <SelectItem value="asc">{t('cat.asc')}</SelectItem>
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button variant="ghost" onClick={clearFilters} className="text-[#0C63BF] gap-1 text-sm" data-testid="clear-filters-btn">
              <X className="w-4 h-4" /> {t('cat.reset')}
            </Button>
          )}
        </div>

        {/* Expanded filters */}
        {showFilters && (
          <div className="bg-white rounded-2xl border border-[#E8F4FD] p-3 sm:p-5 mb-4 sm:mb-6" data-testid="filter-panel">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
              <Select value={filters.brand || "all"} onValueChange={v => updateFilter('brand', v === "all" ? '' : v)}>
                <SelectTrigger className="rounded-xl border-[#D6E8F0]" data-testid="filter-brand"><SelectValue placeholder={t('cat.brand')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('cat.all_brands')}</SelectItem>
                  {brands.map(b => <SelectItem key={b} value={b}>{b.charAt(0).toUpperCase() + b.slice(1)}</SelectItem>)}
                </SelectContent>
              </Select>

              <Select value={filters.shape || "all"} onValueChange={v => updateFilter('shape', v === "all" ? '' : v)}>
                <SelectTrigger className="rounded-xl border-[#D6E8F0]" data-testid="filter-shape"><SelectValue placeholder={t('cat.shape')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('cat.all_shapes')}</SelectItem>
                  <SelectItem value="ronde">{t('cat.shape_round')}</SelectItem>
                  <SelectItem value="diamant">{t('cat.shape_diamond')}</SelectItem>
                  <SelectItem value="goutte">{t('cat.shape_teardrop')}</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filters.play_type || "all"} onValueChange={v => updateFilter('play_type', v === "all" ? '' : v)}>
                <SelectTrigger className="rounded-xl border-[#D6E8F0]" data-testid="filter-play-type"><SelectValue placeholder={t('cat.play_type')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('cat.all_types')}</SelectItem>
                  <SelectItem value="control">{t('cat.type_control')}</SelectItem>
                  <SelectItem value="puissance">{t('cat.type_power')}</SelectItem>
                  <SelectItem value="polyvalent">{t('cat.type_hybrid')}</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filters.weight || "all"} onValueChange={v => updateFilter('weight', v === "all" ? '' : v)}>
                <SelectTrigger className="rounded-xl border-[#D6E8F0]" data-testid="filter-weight"><SelectValue placeholder={t('cat.weight')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('cat.all_weights')}</SelectItem>
                  <SelectItem value="340-355g">{t('cat.w_light')}</SelectItem>
                  <SelectItem value="355-370g">{t('cat.w_medium')}</SelectItem>
                  <SelectItem value="370-385g">{t('cat.w_heavy')}</SelectItem>
                  <SelectItem value="385g+">{t('cat.w_very_heavy')}</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filters.min_condition?.toString() || "all"} onValueChange={v => updateFilter('min_condition', v === "all" ? '' : v)}>
                <SelectTrigger className="rounded-xl border-[#D6E8F0]" data-testid="filter-condition"><SelectValue placeholder={t('cat.min_condition')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('cat.all_conditions')}</SelectItem>
                  <SelectItem value="9">{t('cat.c_excellent')}</SelectItem>
                  <SelectItem value="7">{t('cat.c_very_good')}</SelectItem>
                  <SelectItem value="5">{t('cat.c_good')}</SelectItem>
                  <SelectItem value="3">{t('cat.c_fair')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Price Range Slider */}
            <div className="mt-3 sm:mt-4 px-1" data-testid="price-range-slider-container">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-[#033871]">{t('cat.price_range')}</label>
                <span className="text-xs font-bold text-[#0C63BF]" data-testid="price-range-label">
                  {filters.min_price ? `${filters.min_price}€` : '0€'} — {filters.max_price ? `${filters.max_price}€` : '500€+'}
                </span>
              </div>
              <Slider
                value={[parseInt(filters.min_price) || 0, parseInt(filters.max_price) || 500]}
                onValueChange={([min, max]) => {
                  setFilters(prev => ({
                    ...prev,
                    min_price: min > 0 ? String(min) : '',
                    max_price: max < 500 ? String(max) : '',
                    page: 1,
                  }));
                }}
                min={0}
                max={500}
                step={5}
                className="w-full [&_[data-radix-slider-track]]:bg-[#E8F4FD] [&_[data-radix-slider-range]]:bg-[#0C63BF] [&_[data-radix-slider-thumb]]:border-[#0C63BF] [&_[data-radix-slider-thumb]]:bg-white [&_[data-radix-slider-thumb]]:w-5 [&_[data-radix-slider-thumb]]:h-5 [&_[data-radix-slider-thumb]]:shadow-md"
                data-testid="price-range-slider"
              />
              <div className="flex justify-between mt-1">
                <span className="text-[10px] text-[#B8D4E3]">0€</span>
                <span className="text-[10px] text-[#B8D4E3]">500€+</span>
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden">
                <div className="aspect-[4/3] bg-[#E8F4FD] animate-pulse" />
                <div className="p-4 space-y-2.5">
                  <div className="h-2.5 bg-[#E8F4FD] rounded-full w-1/3 animate-pulse" />
                  <div className="h-3.5 bg-[#E8F4FD] rounded-full w-2/3 animate-pulse" />
                  <div className="h-5 bg-[#E8F4FD] rounded-full w-1/4 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-24">
            <div className="w-20 h-20 bg-[#F0F7FA] rounded-3xl flex items-center justify-center mx-auto mb-5">
              <Search className="w-10 h-10 text-[#B8D4E3]" />
            </div>
            <h3 className="text-xl font-bold text-[#033871] mb-2">{t('cat.no_results')}</h3>
            <p className="text-[#5A8BA8] mb-6 text-sm">{t('cat.no_results_sub')}</p>
            {hasActiveFilters && (
              <Button onClick={clearFilters} variant="outline" className="rounded-xl border-[#0C63BF] text-[#0C63BF]" data-testid="empty-clear-filters-btn">
                {t('cat.reset_filters')}
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5" data-testid="listings-grid">
              {listings.map(l => <ListingCard key={l.id} listing={l} />)}
            </div>
            {total > 24 && (
              <div className="flex justify-center gap-2 mt-10">
                {Array.from({ length: Math.ceil(total / 24) }, (_, i) => (
                  <Button key={i} variant={filters.page === i + 1 ? "default" : "outline"}
                    size="sm" className="rounded-xl min-w-[40px]"
                    onClick={() => setFilters(prev => ({ ...prev, page: i + 1 }))}>
                    {i + 1}
                  </Button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CataloguePage;
